import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  X, HeartHandshake, Clock, Zap, AlertTriangle, CheckCircle2, Sparkles,
} from "lucide-react";
import { HomeOSAvatar } from "./HomeOSAvatar";
import { useHomeOSState } from "../state/HomeOSStateProvider";
import { MOOD_ZONES } from "../config/homeOsTheme";
import {
  canSwapTask, getSwapWarning, buildSwapPatch, SWAP_REASON_CHIPS,
} from "../utils/taskSwapUtils";

/* ════════════════════════════════════════════════════════════════════════
   SWAP TASK MODAL
   Works on both household tasks (state.tasks) and cleaning tasks
   (state.cleaningTasks). The kind is auto-detected from props.
   ════════════════════════════════════════════════════════════════════════ */

export function SwapTaskModal({
  open,
  onClose,
  task,
  taskKind = "task", // "task" | "cleaning" | "plan"
  onApply,           // optional override (used by DelegateCleaningModal plan rows)
}) {
  const state = useHomeOSState();
  const { members, updateTask, updateCleaningTask, moodCheckins } = state;
  const [newMemberId, setNewMemberId] = useState(null);
  const [reason, setReason] = useState("");

  const eligible = useMemo(
    () => (members || []).filter(m => m.ageGroup !== "Baby" && m.ageGroup !== "Pet" && m.id !== task?.assignedTo),
    [members, task]
  );
  const newMember = useMemo(() => members.find(m => m.id === newMemberId), [members, newMemberId]);
  const currentAssignee = members.find(m => m.id === task?.assignedTo);

  const can = canSwapTask({ task, newMember });
  const warnings = useMemo(
    () => (newMember && task ? getSwapWarning({ task, newMember, state }) : []),
    [task, newMember, state]
  );
  const hasStrongWarning = warnings.some(w => w.level === "strong");

  const handleConfirm = () => {
    if (!task || !newMember) return;
    const patch = buildSwapPatch({ task, newMemberId: newMember.id, reason });
    if (onApply) {
      onApply({ patch, newMember, reason });
      toast.success("Swap updated in this plan.");
    } else if (taskKind === "cleaning") {
      updateCleaningTask(task.id, patch);
      toast.success(`Beautiful. ARA moved this task to ${newMember.name}.`);
    } else {
      updateTask(task.id, patch);
      toast.success(`Beautiful. ARA moved this task to ${newMember.name}.`);
    }
    cleanupAndClose();
  };

  const cleanupAndClose = () => {
    setNewMemberId(null);
    setReason("");
    onClose();
  };

  /* ESC to close */
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") cleanupAndClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open || !task) return null;

  const taskEnergy = task.energy || task.energyRequired || "Low";
  const taskMinutes = task.estimatedMinutes ?? null;
  const due = task.dueDate || task.dueTime ? `${task.dueDate || ""}${task.dueTime ? ` · ${task.dueTime}` : ""}` : null;
  const currentMood = currentAssignee ? (moodCheckins || []).find(c => c.memberId === currentAssignee.id && (c.createdAt || "").slice(0, 10) === new Date().toISOString().slice(0, 10)) : null;

  return (
    <div className="fixed inset-0 z-[55] flex items-end sm:items-center justify-center" data-testid="swap-task-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={cleanupAndClose} />

      <div className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto bg-white dark:bg-slate-900 rounded-t-[28px] sm:rounded-[28px] shadow-none font-home animate-fadeUp">
        {/* Header */}
        <div className="sticky top-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm z-10 px-6 pt-6 pb-4 border-b border-gray-100 dark:border-slate-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl    flex items-center justify-center shadow-sm">
                <HeartHandshake className="w-5 h-5 text-white" strokeWidth={1.5} />
              </div>
              <div>
                <h2 className="text-xl font-extrabold text-gray-900 dark:text-gray-50">Swap task</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Choose who is taking this job so ARA can rebalance the household load.</p>
              </div>
            </div>
            <button onClick={cleanupAndClose} className="w-8 h-8 rounded-xl bg-gray-100 dark:bg-slate-800 flex items-center justify-center text-gray-400 hover:text-gray-600" data-testid="swap-close">
              <X className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>

        {/* Task summary */}
        <div className="px-6 pt-5">
          <div className="rounded-2xl    dark: dark: border border-[color:var(--ara-line-soft)] dark:border-pink-800 p-4">
            <p className="text-[11px] font-extrabold uppercase tracking-wider text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]">Task</p>
            <p className="text-xl font-extrabold text-gray-900 dark:text-gray-50 leading-tight mt-0.5" data-testid="swap-task-title">{task.title}</p>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
              {(task.room || task.category) && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/70 dark:bg-slate-800/60 border border-[color:var(--ara-line-soft)] dark:border-pink-800 font-extrabold text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]">
                  {task.room || task.category}
                </span>
              )}
              <span className="inline-flex items-center gap-1 text-gray-600 dark:text-gray-300 font-bold"><Zap className="w-3 h-3" strokeWidth={3} /> {taskEnergy} energy</span>
              {taskMinutes != null && <span className="inline-flex items-center gap-1 text-gray-600 dark:text-gray-300 font-bold"><Clock className="w-3 h-3" strokeWidth={3} /> {taskMinutes}m</span>}
              {due && <span className="inline-flex items-center gap-1 text-gray-600 dark:text-gray-300 font-bold">Due {due}</span>}
            </div>
            {currentAssignee && (
              <div className="mt-3 flex items-center gap-2">
                <span className="text-xs font-bold text-gray-500 dark:text-gray-400">Currently:</span>
                <HomeOSAvatar member={currentAssignee} size="xs" showRing={false} />
                <span className="text-sm font-extrabold text-gray-800 dark:text-gray-100">{currentAssignee.name}</span>
                {currentMood && (
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold ${MOOD_ZONES[currentMood.zone]?.tile} ${MOOD_ZONES[currentMood.zone]?.text}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${MOOD_ZONES[currentMood.zone]?.dot}`} />{currentMood.moodLabel}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Member picker */}
        <div className="px-6 pt-5">
          <p className="text-[11px] font-extrabold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">Who is taking this?</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5" data-testid="swap-member-grid">
            {eligible.length === 0 && (
              <p className="col-span-full text-sm text-gray-500 italic">No other eligible family members to take this.</p>
            )}
            {eligible.map(m => {
              const active = newMemberId === m.id;
              const mood = (moodCheckins || []).find(c => c.memberId === m.id && (c.createdAt || "").slice(0, 10) === new Date().toISOString().slice(0, 10));
              const z = mood ? MOOD_ZONES[mood.zone] : null;
              const load = ((state.tasks || []).filter(t => t.assignedTo === m.id && t.status !== "Done").length) +
                           ((state.cleaningTasks || []).filter(t => t.assignedTo === m.id && t.status !== "Done").length);
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setNewMemberId(m.id)}
                  className={`text-left rounded-2xl border-2 p-3 transition-all ${active ? "border-rose-400 bg-transparent dark:bg-transparent shadow-md" : "border-gray-200 dark:border-slate-700 hover:border-[color:var(--ara-line-soft)] bg-white dark:bg-slate-800/60"}`}
                  data-testid={`swap-member-${m.id}`}
                  aria-pressed={active}
                >
                  <div className="flex items-center gap-2">
                    <HomeOSAvatar member={m} size="md" />
                    <div className="min-w-0">
                      <p className="text-sm font-extrabold text-gray-900 dark:text-gray-50 truncate">{m.name}</p>
                      <p className="text-[10px] font-bold text-gray-500 dark:text-gray-400 truncate">{m.role}</p>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-1 text-[10px] font-extrabold">
                    {mood ? (
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full ${z.tile} ${z.text}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${z.dot}`} />{mood.moodLabel}
                      </span>
                    ) : (
                      <span className="px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-slate-700 text-gray-500">No check-in</span>
                    )}
                    <span className="px-1.5 py-0.5 rounded-full bg-transparent dark:bg-transparent text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]">{m.energy} energy</span>
                    <span className="px-1.5 py-0.5 rounded-full bg-transparent dark:bg-transparent text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]">{load} open</span>
                  </div>
                </button>
              );
            })}
          </div>
          {!can.ok && newMember && (
            <p className="mt-2 text-xs font-bold text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]" data-testid="swap-cannot-reason">{can.reason}</p>
          )}
        </div>

        {/* Warnings */}
        {warnings.length > 0 && (
          <div className="px-6 pt-4 space-y-2" data-testid="swap-warnings">
            {warnings.map((w, idx) => (
              <div
                key={idx}
                className={`flex items-start gap-2.5 rounded-2xl border px-3.5 py-3 ${w.level === "strong" ? "bg-transparent dark:bg-transparent border-[color:var(--ara-line-soft)] dark:border-rose-800" : "bg-transparent dark:bg-transparent border-[color:var(--ara-line-soft)] dark:border-amber-800"}`}
                data-testid={`swap-warning-${idx}`}
              >
                <AlertTriangle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${w.level === "strong" ? "text-[color:var(--ara-ink-muted)]" : "text-[color:var(--ara-ink-muted)]"}`} strokeWidth={1.5} />
                <p className={`text-sm font-bold ${w.level === "strong" ? "text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]" : "text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]"}`}>
                  {w.level === "strong" ? "Heads up — " : "ARA notice: "}{w.text}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Preview */}
        {newMember && can.ok && (
          <div className="px-6 pt-4">
            <div className="rounded-2xl bg-transparent dark:bg-transparent border border-[color:var(--ara-line-soft)] dark:border-emerald-800 px-4 py-3" data-testid="swap-preview">
              <p className="text-sm font-extrabold text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]">
                ARA will move this task from <span className="underline">{currentAssignee?.name || "Unassigned"}</span> to <span className="underline">{newMember.name}</span>.
              </p>
            </div>
          </div>
        )}

        {/* Reason */}
        <div className="px-6 pt-4 pb-2">
          <p className="text-[11px] font-extrabold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">Why are you swapping? <span className="font-bold normal-case text-gray-400">(optional)</span></p>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {SWAP_REASON_CHIPS.map(chip => {
              const active = reason === chip;
              return (
                <button
                  key={chip}
                  type="button"
                  onClick={() => setReason(active ? "" : chip)}
                  className={`px-3 py-1.5 rounded-full text-xs font-extrabold border transition-colors ${active ? "bg-transparent text-white border-rose-500" : "bg-white dark:bg-slate-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-slate-700 hover:border-[color:var(--ara-line-soft)]"}`}
                  data-testid={`swap-reason-chip-${chip.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  aria-pressed={active}
                >
                  {chip}
                </button>
              );
            })}
          </div>
          <input
            type="text"
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="Example: I have time now, they need a break, I'm already in the kitchen…"
            className="w-full rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2.5 text-sm font-medium text-gray-800 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-rose-300"
            data-testid="swap-reason-input"
          />
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm px-6 py-4 border-t border-gray-100 dark:border-slate-800 flex items-center gap-2 flex-wrap mt-4">
          <button onClick={cleanupAndClose} className="px-3.5 py-2.5 rounded-2xl text-sm font-bold text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-800" data-testid="swap-cancel">
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!newMember || !can.ok || hasStrongWarning}
            className="ml-auto inline-flex items-center gap-1.5 bg-transparent hover:opacity-80 disabled:opacity-50 text-white font-extrabold text-sm px-5 py-2.5 rounded-2xl shadow-md hover:shadow-none active:scale-[0.97]"
            data-testid="swap-confirm"
          >
            <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} />
            Confirm swap
          </button>
        </div>

        <div className="px-6 pb-5 -mt-2">
          <p className="text-[11px] text-gray-400 italic flex items-center gap-1">
            <Sparkles className="w-3 h-3" strokeWidth={3} />
            Local-only. Swap saves on this device.
          </p>
        </div>
      </div>
    </div>
  );
}

export default SwapTaskModal;
